import React, {ChangeEvent, useCallback, useState} from 'react'
import {useLocation} from 'react-router-dom'
import MemberRepo from '../../../Repo/MemberRepo'
import {NavigatorForMemberPage} from '../../../Navigator/NavigatorForMemberPage'
import {MemberInfoForConfirmPage, MemberInfoForInputPage} from '../../../Props/MemberRegisterPageLocationStates'
import {SearchPostCode} from '../../../../Common/SearchPostCode'
import {MBMemberSelect} from '../../Atoms/MBMemberSelect'
import {CALENDAR, PAYMENT} from '../../../../Common/Constants'
import {MailMagazine} from '../../../DTO/MailMagazineDTO'
import {Signboard} from '../../../DTO/SignboardDTO'
import {validateAgreeCheck, validatePassword} from '../../../Utils/Validations/MembersValidation'
import {ExtendMetaDataErrors, validateMemberSignupInput, ValidationSignupInput} from '../../../Utils/Validations/ValidateMembersSignupInput'
import {PaymentMethod} from '../../../DTO/PaymentMethodDTO'
import {MBMemberError} from '../../../Common/MBMemberError'
import {ContractInputInfo, ContractInputInfoAll, ContractMethod, ContractTerm, ErrorMessageForInvalidCard, ErrorMessagesForSignupContract, findAmountByTermAndMethod, isFreeBucket, PaymentInputDetail} from '../../../Props/ContractInputInfo'
import {MailMagazineSelectContentBlock} from '../../Components/InputContentBlocks/MailMagazineSelectContentBlock'
import {PaymentInformation, RegisterOrganizationContentBlock} from '../../Components/RegisterOrganizationContentBlocks/RegisterOrganizationContentBlock'
import {SelectContractTermContents} from '../../Components/PaymentsRelations/InputContentBlocks/SelectContractTermContents'
import {animateScroll} from 'react-scroll'
import {PageTitle} from '../../Molecules/BaseMembersPages/PageTitle'
import {ErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/ErrorSummary'
import {MembersPageTemplate} from '../../Molecules/BaseMembersPages/MembersPageTemplate'
import StepGuide from '../../Molecules/StepGuide/StepGuide'
import {PasswordInputContent} from '../../Components/InputContentBlocks/PasswordInputContent'
import {PIMPageActions} from '../../Atoms/Layouts/PIMPageActions'
import {PIMSubmitButton} from '../../Atoms/Buttons/Bases/PIMSubmitButton'
import {MBContentBodyTypography, MBContentSubTitleTypography} from '../../Atoms/Typographies'
import {Spacer} from '../../../../Common/Components/Spacer'
import {MBContentBodyDivider} from '../../Atoms/Dividers'
import {PIMStack} from '../../Atoms/Layouts/Bases/PIMStack'
import {MBRequiredBadge} from '../../Atoms/Badges'
import {PIMWarningMessages} from '../../Atoms/Labels/PIMWarningMessages'
import {AgreementCheckBoxContentBlock} from '../../Components/InputContentBlocks/AgreementCheckBoxContentBlock'
import {PIMCard} from '../../Atoms/Cards/PIMCard'
import {MembersAgreement} from '../../Ecosystems/MembersAgreement'
import MemberAgreementRepo from '../../../Repo/MemberAgreementRepo'
import {SignupSelectPaymentMethodContents} from './Components/SignupSelectPaymentMethodContents'
import {InputErrorSummary} from '../../Molecules/ErrorsAndAlert/ErrorSummary/InputErrorSummary'
import {ContactAddressInputContents} from '../../Components/InputContentBlocks/AddressInputs/ContactAddressInputContents'
import {Optional} from '../../../../Common/TypeHelper'
import {EnrollmentReasonSelectContentBlock, EnrollmentReasonSelectedInfo} from '../../Components/InputContentBlocks/EnrollmentReasonSelectContentBlock'
import {MemberNameInputs} from '../../Components/InputContentBlocks/MemberNameInputs'
import BucketHeader from '../../../DTO/BucketHeader'
import {GenderInput} from './Components/GenderInput'
import {useTranslation} from 'react-i18next'
import {ExtendMetaData, ExtendMetaDataInputInfo} from '../../../DTO/ExtendMetaDataDTO'
import {ExtendMetaDataInputBlock} from './Components/ExtendMetaDataInputBlock'

type MembersSignupInputPageProps = {
  navigator: NavigatorForMemberPage
  memberRepo: MemberRepo
  memberAgreementRepo: MemberAgreementRepo
  searchPostCode: SearchPostCode
}

export type InputPageLocationState = {
  isInvalidCardError: boolean
  isMobileCarrierError: boolean
  memberInfo: MemberInfoForInputPage
  signboard: Signboard
  paymentMethodList: PaymentMethod[]
  contractInputInfo: ContractInputInfo
  extendMetaData: ExtendMetaData[]
  extendMetaDataInputInfo?: ExtendMetaDataInputInfo
}

type State = {
  familyName: string
  firstName: string
  familyNameKana: string
  firstNameKana: string
  gender: string
  birthdayYear: string
  birthdayMonth: string
  birthdayDay: string
  postCode: string
  addressPrefectureCode: string
  addressCity: string
  addressStreet: string
  addressBuilding: string
  phoneMobile: string
  phoneTel: string
  password: string
  agreedIdList: number[]
  mailMagazineIdCheckedList: number[]
  enrollmentReasonSelectedInfo: Optional<EnrollmentReasonSelectedInfo>
}

type ErrorState = {
  isInputError: boolean
  agreeCheckErrors: string[]
  familyNameErrors: string[]
  firstNameErrors: string[]
  familyNameKanaErrors: string[]
  firstNameKanaErrors: string[]
  genderErrors: string[]
  birthdayErrors: string[]
  birthdayYearErrors: string[]
  birthdayMonthErrors: string[]
  birthdayDayErrors: string[]
  postCodeErrors: string[]
  addressPrefectureCodeErrors: string[]
  addressCityErrors: string[]
  addressStreetErrors: string[]
  addressBuildingErrors: string[]
  phoneMobileErrors: string[]
  phoneTelErrors: string[]
  passwordErrors: string[]
  contractErrors: ErrorMessagesForSignupContract
  enrollmentReasonErrors: string[]
  extendMetaDataErrors: Optional<ExtendMetaDataErrors>
}

export const MembersSignupInputPage: React.VFC<MembersSignupInputPageProps> = (props) => {
  const {t} = useTranslation('member')
  const [isSubmitButtonDisabled, setIsSubmitButtonDisabled] = useState(false)

  const location = useLocation<InputPageLocationState>()
  const locationState: InputPageLocationState = location.state

  const [contractInputInfoState, setContractInputInfoState] = useState<ContractInputInfoAll>({
    term: isFreeBucket(locationState?.paymentMethodList)
      ? 'permanent'
      : (locationState?.contractInputInfo?.term || ''),
    method: isFreeBucket(locationState?.paymentMethodList)
      ? 'none'
      : locationState?.contractInputInfo?.method,
    amount: findAmountByTermAndMethod(
      locationState?.paymentMethodList,
      locationState?.contractInputInfo?.term,
      locationState?.contractInputInfo?.method,
    ),
    creditCard: {
      creditCardType: 'newCard',
      newCreditCardInfo: {
        cardNumber:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.cardNumber || '',
        expireMonth:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.expireMonth || '',
        expireYear:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.expireYear || '',
        holderName:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.holderName || '',
        securityCode:
          locationState?.contractInputInfo?.creditCard?.newCreditCardInfo.securityCode || '',
      },
      myCreditCardInfo: {selectedCardId: '', securityCodeMap: []},
    },
    mobileCarrier: {
      carrierKey: locationState?.contractInputInfo?.mobileCarrier?.carrierKey || '',
    },
    convenienceStore: {
      convenienceStoreKey:
        locationState?.contractInputInfo?.convenienceStore?.convenienceStoreKey || '',
    },
  })

  const [state, setState] = useState<State>({
    familyName: locationState?.memberInfo?.familyName || '',
    firstName: locationState?.memberInfo?.firstName || '',
    familyNameKana: locationState?.memberInfo?.familyNameKana || '',
    firstNameKana: locationState?.memberInfo?.firstNameKana || '',
    gender: locationState?.memberInfo?.gender || '',
    birthdayYear: locationState?.memberInfo?.birthday?.split('/')[0] || '',
    birthdayMonth: locationState?.memberInfo?.birthday?.split('/')[1] || '',
    birthdayDay: locationState?.memberInfo?.birthday?.split('/')[2] || '',
    postCode: locationState?.memberInfo?.postCode || '',
    addressPrefectureCode: locationState?.memberInfo?.addressPrefectureCode || '',
    addressCity: locationState?.memberInfo?.addressCity || '',
    addressStreet: locationState?.memberInfo?.addressStreet || '',
    addressBuilding: locationState?.memberInfo?.addressBuilding || '',
    phoneMobile: locationState?.memberInfo?.phoneMobile || '',
    phoneTel: locationState?.memberInfo?.phoneTel || '',
    password: locationState?.memberInfo?.password || '',
    agreedIdList: locationState?.memberInfo?.agreedIdList || [],
    mailMagazineIdCheckedList: location.state?.memberInfo?.mailMagazineIdCheckedList
      || getDefaultMailMagazineIdCheckList(locationState?.signboard?.mailMagazineList || []),
    enrollmentReasonSelectedInfo: locationState?.memberInfo?.enrollmentReasonSelectedInfo ||
      getDefaultEnrollmentReasonSelectedInfo(locationState?.signboard?.mainService),
  })

  const [extendMetaDataInputState, setExtendMetaDataInputState] =
    useState<Optional<ExtendMetaDataInputInfo>>(
      locationState?.extendMetaDataInputInfo ??
      getDefaultExtendMetaDataInputInfo(locationState?.extendMetaData),
    )

  const [errorState, setErrorState] = useState<ErrorState>({
    isInputError: false,
    agreeCheckErrors:
      (locationState?.memberInfo?.agreedIdList !== undefined)
        ? validateAgreeCheck(locationState.memberInfo.agreedIdList)
        : [],
    familyNameErrors: [],
    firstNameErrors: [],
    familyNameKanaErrors: [],
    firstNameKanaErrors: [],
    genderErrors: [],
    birthdayErrors: [],
    birthdayYearErrors: [],
    birthdayMonthErrors: [],
    birthdayDayErrors: [],
    postCodeErrors: [],
    addressPrefectureCodeErrors: [],
    addressCityErrors: [],
    addressStreetErrors: [],
    addressBuildingErrors: [],
    phoneMobileErrors: [],
    phoneTelErrors: [],
    passwordErrors: locationState?.isMobileCarrierError ? validatePassword('') : [],
    contractErrors: {
      invalidCardErrors: ErrorMessageForInvalidCard(
        locationState?.contractInputInfo?.creditCard?.creditCardType,
        locationState?.isInvalidCardError,
      ),
      paymentInputTermErrors: [],
      paymentInputMethodErrors: [],
      creditCardNumberErrors: [],
      creditCardExpireMonthErrors: [],
      creditCardExpireYearErrors: [],
      creditCardFutureExpireDateErrors: [],
      creditCardHolderNameErrors: [],
      creditCardSecurityCodeErrors: [],
      convenienceStoreErrors: [],
      mobileCarrierErrors: locationState?.isMobileCarrierError
        ? ['決済が完了しませんでした。再度お手続きを実施ください。']
        : [],
    },
    enrollmentReasonErrors: [],
    extendMetaDataErrors: undefined,
  })

  const [showAgreement, setShowAgreement] = useState(false)

  const [showPasswordChecked, setShowPasswordChecked] = useState(false)

  const handleAgreementButtonClick = useCallback((): void => {
    setShowAgreement(true)
  }, [])

  const handleAgree = useCallback((agreedIdList: number[]): void => {
    props.navigator.changeMembersSignupInputPageState({
        ...locationState.memberInfo,
        agreedIdList: agreedIdList,
      } as MemberInfoForInputPage,
      locationState.signboard,
      locationState.paymentMethodList,
      locationState.extendMetaData,
      locationState.contractInputInfo,
      locationState.extendMetaDataInputInfo,
    )
    setState((prevState) => {
      return {
        ...prevState,
        agreedIdList: agreedIdList,
      }
    })
    setErrorState((prevState) => {
      return {
        ...prevState,
        agreeCheckErrors: validateAgreeCheck(agreedIdList),
      }
    })
    setShowAgreement(false)
  }, [props.navigator, locationState])

  if (isInvalidLocationState(locationState)) {
    throw new MBMemberError('InvalidOperation')
  }

  function getDefaultMailMagazineIdCheckList(mailMagazineList: MailMagazine[]): number[] {
    if (!mailMagazineList) {
      return []
    }
    return mailMagazineList.map((mailMagazine) => mailMagazine.id)
  }

  function set20yearsOld(e: ChangeEvent<HTMLSelectElement>): void {
    if (state.birthdayYear === '') {
      e.target.children.item(21)?.setAttribute('selected', 'true')
      updateState('birthdayYear', e.target.value)
    }
  }

  function goToMembersSignupInputConfirmPage(): void {
    setShowPasswordChecked(false)
    setIsSubmitButtonDisabled(true)

    const inputForValidation: ValidationSignupInput = {
      ...state,
      familyName: state.familyName.trim(),
      firstName: state.firstName.trim(),
      familyNameKana: state.familyNameKana.trim(),
      firstNameKana: state.firstNameKana.trim(),
      postCode: state.postCode.trim(),
      addressCity: state.addressCity.trim(),
      addressStreet: state.addressStreet.trim(),
      addressBuilding: addressBuilding.trim(),
      phoneMobile: phoneMobile.trim(),
      phoneTel: phoneTel.trim(),
      paymentInputTerm: contractInputInfoState?.term,
      paymentInputMethod: contractInputInfoState?.method,
      creditCardType: contractInputInfoState?.creditCard?.creditCardType || '',
      creditCardNumber:
        contractInputInfoState?.creditCard?.newCreditCardInfo.cardNumber || '',
      creditCardExpireMonth:
        contractInputInfoState?.creditCard?.newCreditCardInfo.expireMonth || '',
      creditCardExpireYear:
        contractInputInfoState?.creditCard?.newCreditCardInfo.expireYear || '',
      creditCardHolderName:
        contractInputInfoState?.creditCard?.newCreditCardInfo.holderName || '',
      creditCardSecurityCode:
        contractInputInfoState?.creditCard?.newCreditCardInfo.securityCode || '',
      convenienceStore: contractInputInfoState?.convenienceStore?.convenienceStoreKey || '',
      mobileCarrier: contractInputInfoState?.mobileCarrier?.carrierKey || '',
      enrollmentReasonValidateParam: {
        candidates: signboard.mainService.enrollmentReasonList || [],
        selected: state.enrollmentReasonSelectedInfo,
      },
      extendMetaDataParam: {
        extendMetaData: locationState?.extendMetaData,
        inputState: extendMetaDataInputState,
      },
    }
    const {
      enrollmentReasonValidateParam,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      extendMetaDataParam,
      ...restForState
    } = inputForValidation
    const inputForState = {
      ...restForState,
      enrollmentReasonSelectedInfo: enrollmentReasonValidateParam.selected,
    }
    setState((prevState) => ({
      ...prevState,
      ...inputForState,
    }))

    const validationResult = validateMemberSignupInput(inputForValidation)

    if (validationResult.hasError) {
      setIsSubmitButtonDisabled(false)
      setErrorState({
        isInputError: true,
        agreeCheckErrors: validationResult.errors.agreedIdList,
        familyNameErrors: validationResult.errors.familyName,
        firstNameErrors: validationResult.errors.firstName,
        familyNameKanaErrors: validationResult.errors.familyNameKana,
        firstNameKanaErrors: validationResult.errors.firstNameKana,
        genderErrors: validationResult.errors.gender,
        birthdayErrors: validationResult.errors.birthdayCombination,
        birthdayYearErrors: validationResult.errors.birthdayYear,
        birthdayMonthErrors: validationResult.errors.birthdayMonth,
        birthdayDayErrors: validationResult.errors.birthdayDay,
        postCodeErrors: validationResult.errors.postCode,
        addressPrefectureCodeErrors: validationResult.errors.addressPrefectureCode,
        addressCityErrors: validationResult.errors.addressCity,
        addressStreetErrors: validationResult.errors.addressStreet,
        addressBuildingErrors: validationResult.errors.addressBuilding,
        phoneMobileErrors: validationResult.errors.phoneMobile,
        phoneTelErrors: validationResult.errors.phoneTel,
        passwordErrors: validationResult.errors.password,
        contractErrors: {
          invalidCardErrors: [],
          paymentInputTermErrors: validationResult.errors.paymentInputTerm,
          paymentInputMethodErrors: validationResult.errors.paymentInputMethod,
          creditCardNumberErrors: validationResult.errors.creditCardNumber,
          creditCardExpireMonthErrors: validationResult.errors.creditCardExpireMonth,
          creditCardExpireYearErrors: validationResult.errors.creditCardExpireYear,
          creditCardFutureExpireDateErrors: validationResult.errors.creditCardFutureExpireDate,
          creditCardHolderNameErrors: validationResult.errors.creditCardHolderName,
          creditCardSecurityCodeErrors: validationResult.errors.creditCardSecurityCode,
          convenienceStoreErrors: validationResult.errors.convenienceStore,
          mobileCarrierErrors: validationResult.errors.mobileCarrier,
        },
        enrollmentReasonErrors: validationResult.errors.enrollmentReasonValidateParam,
        extendMetaDataErrors: validationResult.errors.extendMetaData,
      })
      animateScroll.scrollToTop()
      return
    }

    const {
      birthdayYear,
      birthdayMonth,
      birthdayDay,
      paymentInputTerm,
      paymentInputMethod,
      creditCardNumber,
      creditCardExpireMonth,
      creditCardExpireYear,
      creditCardHolderName,
      creditCardSecurityCode,
      convenienceStore,
      mobileCarrier,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      creditCardType,
      ...rest
    } = inputForState
    const memberRegisterInformationProps: MemberInfoForConfirmPage = {
      mail: locationState.memberInfo.mail,
      invitationId: locationState.memberInfo.invitationId,
      birthday: birthdayYear + '/' + birthdayMonth + '/' + birthdayDay,
      mailMagazineIdCheckedList: state.mailMagazineIdCheckedList,
      ...rest,
    }

    if (!contractInputInfoState?.creditCard) {
      return
    }

    const contractInputInfo: ContractInputInfo = {
      term: paymentInputTerm ?? '',
      method: paymentInputMethod,
      amount: contractInputInfoState.amount,

      creditCard: {
        ...contractInputInfoState.creditCard,
        newCreditCardInfo: {
          cardNumber: creditCardNumber,
          expireMonth: creditCardExpireMonth,
          expireYear: creditCardExpireYear,
          holderName: creditCardHolderName,
          securityCode: creditCardSecurityCode,
        },
      },

      mobileCarrier: {
        carrierKey: mobileCarrier,
      },

      convenienceStore: {
        convenienceStoreKey: convenienceStore,
      },
    }


    props.navigator.goToMembersSignupInputConfirmPage(
      memberRegisterInformationProps,
      locationState.signboard,
      locationState.paymentMethodList,
      contractInputInfo,
      locationState.extendMetaData,
      extendMetaDataInputState,
    )
  }

  function updateState(name: string, value: string): void {
    setState((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  function handleChangeSelect(e: React.ChangeEvent<HTMLSelectElement>): void {
    const {name, value} = e.target
    updateState(name, value)
  }

  const handlePasswordInputChange = useCallback((password: string) => {
    updateState('password', password)
  }, [])

  if (!locationState
    || !locationState.memberInfo?.mail || !locationState.memberInfo?.invitationId
    || !locationState.signboard?.signupKey || !locationState.signboard?.logoImgUrl
    || !locationState.signboard?.signupName || !locationState.signboard?.mailMagazineList
    || !locationState.paymentMethodList
  ) return null

  const paymentInformationList: PaymentInformation[] =
    locationState.paymentMethodList
      .filter(paymentMethod =>
        Object.keys(PAYMENT.TERMS).includes(paymentMethod.term),
      )
      .map(paymentMethod => ({
        isFreeBucket: paymentMethod.method === 'none',
        termName: PAYMENT.TERMS[paymentMethod.term].displayLabel,
        contractAmount: paymentMethod.amount,
        order: PAYMENT.TERMS[paymentMethod.term].order,
        term: paymentMethod.term,
      }))

  const {
    isMobileCarrierError, isInvalidCardError,
    memberInfo, signboard, paymentMethodList, extendMetaData,
  } = locationState

  const {mail} = memberInfo
  const {
    familyName, firstName,
    familyNameKana, firstNameKana,
    gender,
    birthdayYear, birthdayMonth, birthdayDay,
    postCode,
    addressPrefectureCode, addressCity, addressStreet, addressBuilding,
    phoneMobile, phoneTel,
    password,
    agreedIdList,
    mailMagazineIdCheckedList,
    enrollmentReasonSelectedInfo,
  } = state
  const {
    isInputError,
    agreeCheckErrors,
    familyNameErrors, firstNameErrors,
    familyNameKanaErrors, firstNameKanaErrors,
    genderErrors,
    birthdayErrors, birthdayYearErrors, birthdayMonthErrors, birthdayDayErrors,
    postCodeErrors,
    addressPrefectureCodeErrors, addressCityErrors, addressStreetErrors, addressBuildingErrors,
    phoneMobileErrors, phoneTelErrors,
    passwordErrors,
    contractErrors,
    enrollmentReasonErrors,
    extendMetaDataErrors,
  } = errorState

  if (paymentInformationList.length === 0) {
    throw new MBMemberError('SystemError')
  }

  const birthdayErrorMessages = [
    ...birthdayYearErrors,
    ...birthdayMonthErrors,
    ...birthdayDayErrors,
    ...birthdayErrors,
  ]

  return (
    <MembersPageTemplate hideFooterNavi={true}>
      {showAgreement ? (<div data-testid={'members-agreement-block'}>
        <MembersAgreement
          memberAgreementRepo={props.memberAgreementRepo}
          signboard={signboard}
          onAgreeClick={handleAgree}
          onDisagreeClick={handleAgree}
        />
      </div>) : (<div data-testid={'members-signup-input-block'}>
        <PageTitle>{`${signboard.signupName} 新規登録情報入力`}</PageTitle>

        <InputErrorSummary display={isInputError || isInvalidCardError}/>
        <ErrorSummary
          display={!isInputError && isMobileCarrierError}
          headline={'キャリア決済に失敗しました。'}
          detail={
            '入力項目をご確認の上、再実施いただくか、他の決済方法をご利用ください。\n また、何度もこのメッセージが表示される場合には、各キャリアへ問い合わせをお願いします。'
          }
        />

        <StepGuide activity={'signup'} current={'enter'}/>

        <PIMStack alignItems={'center'} spacing={'16px'}>
          <RegisterOrganizationContentBlock
            signboard={signboard}
            title={t('新規登録情報入力.入会バケット情報.見出し')}
            paymentInformationList={paymentInformationList}
          />

          <AgreementCheckBoxContentBlock
            agreedIdList={agreedIdList}
            agreeCheckErrors={agreeCheckErrors}
            onClick={handleAgreementButtonClick}
          />

          <PIMCard
            headerProps={{title: t('新規登録情報入力.基本情報入力.見出し')}}
            testId={'block-input-basic-information'}
          >
            <PIMStack
              spacing={'18px'}
              divider={<MBContentBodyDivider/>}
              showEndDivider
            >
              <MBContentSubTitleTypography>
                {t('新規登録情報入力.基本情報入力.説明')}
              </MBContentSubTitleTypography>

              <PIMStack spacing={'10px'}>
                <MBContentSubTitleTypography>
                  メールアドレス
                </MBContentSubTitleTypography>
                <MBContentBodyTypography>
                  {mail}
                </MBContentBodyTypography>
              </PIMStack>

              <MemberNameInputs
                memberName={{familyName, firstName, familyNameKana, firstNameKana}}
                memberNameErrors={
                  {familyNameErrors, firstNameErrors, familyNameKanaErrors, firstNameKanaErrors}
                }
                onMemberNameChange={(memberName): void =>
                  setState(prevState => ({
                      ...prevState,
                      ...memberName,
                    }),
                  )}
              />

              <GenderInput
                inputGender={gender}
                genderErrors={genderErrors}
                onChange={(inputGender): void =>
                  setState(prevState => ({
                      ...prevState,
                      gender: inputGender,
                    }),
                  )}
              />

              <PIMStack spacing={'10px'} testId={'block-input-birthday-info'}>
                <PIMStack direction={'row'} alignItems={'center'} spacing={'6px'}>
                  <MBContentSubTitleTypography>生年月日</MBContentSubTitleTypography>
                  <MBRequiredBadge/>
                </PIMStack>
                <PIMStack direction={'row'} alignItems={'center'}>
                  <MBMemberSelect
                    name="birthdayYear"
                    autoComplete="bday-year"
                    value={birthdayYear || ''}
                    hasError={birthdayYearErrors.length > 0 || birthdayErrors.length > 0}
                    onFocus={(e: React.FocusEvent<HTMLSelectElement>): void => {
                      set20yearsOld(e)
                    }}
                    onChange={
                      (e: React.ChangeEvent<HTMLSelectElement>): void => handleChangeSelect(e)
                    }
                    width={'26%'}
                    id="year">
                    <option value="" hidden disabled>年</option>
                    {CALENDAR.YEARS.map((year, i) => (
                      <option key={i} value={year || ''}>{year}</option>
                    ))}
                  </MBMemberSelect>
                  <Spacer mr={'5px'}>
                    <label htmlFor="year" className="year-label">年</label>
                  </Spacer>
                  <MBMemberSelect
                    name="birthdayMonth"
                    autoComplete="bday-month"
                    value={birthdayMonth || ''}
                    hasError={birthdayMonthErrors.length > 0 || birthdayErrors.length > 0}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                      handleChangeSelect(e)
                    }}
                    width={'20%'}
                    id="month">
                    <option value="" hidden disabled>月</option>
                    {CALENDAR.MONTHS.map((month, i) => (
                      <option key={i} value={month || ''}>{month}</option>
                    ))}
                  </MBMemberSelect>
                  <Spacer mr={'5px'}>
                    <label htmlFor="month" className="month-label">月</label>
                  </Spacer>
                  <MBMemberSelect
                    name="birthdayDay"
                    autoComplete="bday-day"
                    value={birthdayDay || ''}
                    hasError={birthdayDayErrors.length > 0 || birthdayErrors.length > 0}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
                      handleChangeSelect(e)
                    }}
                    width={'20%'}
                    id="day">
                    <option value="" hidden disabled>日</option>
                    {CALENDAR.DAYS.map((day, i) => (
                      <option key={i} value={day || ''}>{day}</option>
                    ))}
                  </MBMemberSelect>
                  <label htmlFor="day" className="day-label">日</label>
                </PIMStack>
                {birthdayErrorMessages.length > 0 && (
                  <PIMWarningMessages messages={birthdayErrorMessages}/>
                )}
              </PIMStack>
            </PIMStack>
          </PIMCard>

          <PIMCard
            headerProps={{title: t('新規登録情報入力.連絡先入力.見出し')}}
          >
            <PIMStack
              spacing={'18px'}
              divider={<MBContentBodyDivider/>}
            >
              <MBContentSubTitleTypography>
                {t('新規登録情報入力.連絡先入力.説明')}
              </MBContentSubTitleTypography>

              <ContactAddressInputContents
                activity={'signup'}
                searchPostCode={props.searchPostCode}
                contactAddress={{
                  postCode,
                  addressPrefectureCode,
                  addressCity,
                  addressStreet,
                  addressBuilding,
                  phoneMobile,
                  phoneTel,
                }}
                contactAddressErrors={{
                  postCodeErrors,
                  addressPrefectureCodeErrors,
                  addressCityErrors,
                  addressStreetErrors,
                  addressBuildingErrors,
                  phoneMobileErrors,
                  phoneTelErrors,
                }}
                onChange={(address): void => {
                  setState((prevState) => {
                    return {
                      ...prevState,
                      postCode: address.postCode,
                      addressPrefectureCode: address.addressPrefectureCode ?? '',
                      addressCity: address.addressCity ?? '',
                      addressStreet: address.addressStreet ?? '',
                      addressBuilding: address.addressBuilding ?? '',
                      phoneMobile: address.phoneMobile ?? '',
                      phoneTel: address.phoneTel ?? '',
                    }
                  })
                }}
              />
            </PIMStack>
          </PIMCard>

          {(extendMetaData.length > 0 && extendMetaDataInputState !== undefined) &&
            <ExtendMetaDataInputBlock
              extendMetaData={extendMetaData}
              extendMetaDataInputState={extendMetaDataInputState}
              setExtendMetaDataInputState={
                (inputInfo): void => setExtendMetaDataInputState(inputInfo)
              }
              errorMessages={extendMetaDataErrors}
            />
          }

          <PasswordInputContent
            title={t('新規登録情報入力.パスワード入力.見出し')}
            topMessage={t('新規登録情報入力.パスワード入力.説明')}
            showRequiredBadge={true}
            password={password}
            onChange={handlePasswordInputChange}
            passwordErrors={passwordErrors}
            defaultShowPasswordChecked={showPasswordChecked}
            onCheckboxChange={(checked: boolean): void => {
              setShowPasswordChecked(checked)
            }}
          />

          {(signboard.mailMagazineList.length > 0) && (
            <MailMagazineSelectContentBlock
              data-testid={'block-mail-magazine'}
              title={t('新規登録情報入力.メルマガ選択.見出し')}
              topMessage={t('新規登録情報入力.メルマガ選択.説明')}
              note={t('新規登録情報入力.メルマガ選択.補足')}
              mailMagazineList={signboard.mailMagazineList}
              mailMagazineIdCheckedList={mailMagazineIdCheckedList}
              onChange={(mailMagazineIdCheckedList: number[]): void => {
                setState((prevState) => {
                  return {
                    ...prevState,
                    mailMagazineIdCheckedList: mailMagazineIdCheckedList,
                  }
                })
              }}
            />
          )}

          <EnrollmentReasonSelectContentBlock
            enrollmentReasonInfoList={signboard.mainService.enrollmentReasonList}
            enrollmentReasonSelectedInfo={enrollmentReasonSelectedInfo}
            onChange={(enrollmentReasonSelectedInfo): void => {
              setState(prevState => ({
                ...prevState,
                enrollmentReasonSelectedInfo: enrollmentReasonSelectedInfo,
              }))
            }}
            errorMessages={enrollmentReasonErrors}
          />

          {!isFreeBucket(paymentMethodList) && (<>
            <SelectContractTermContents
              paymentMethodList={paymentMethodList}
              paymentInformationList={paymentInformationList}
              contractInputInfoStateTerm={contractInputInfoState.term}
              onChange={(changeSelectedTerm: ContractTerm): void => {
                setContractInputInfoState((prevState) => {
                  return {
                    ...prevState,
                    term: changeSelectedTerm,
                    method: undefined,
                    amount: 0,
                  }
                })
              }}
              errorMessage={contractErrors}
            />

            <SignupSelectPaymentMethodContents
              paymentMethodList={paymentMethodList}
              contractInputInfoState={contractInputInfoState}
              onChangeSelectedMethod={(changeSelectedMethod: ContractMethod): void => {
                setContractInputInfoState((prevState) => {
                  return {
                    ...prevState,
                    method: changeSelectedMethod,
                    amount: findAmountByTermAndMethod(
                      paymentMethodList, prevState.term, changeSelectedMethod,
                    ),
                  }
                })
              }}
              onChangeInputDetailInfo={(changeInputDetailInfo: PaymentInputDetail): void => {
                setContractInputInfoState((prevState) => {
                  return {
                    ...prevState,
                    creditCard: changeInputDetailInfo.creditCard,
                    mobileCarrier: changeInputDetailInfo.mobileCarrier,
                    convenienceStore: changeInputDetailInfo.convenienceStore,
                  }
                })
              }}
              errorMessages={{
                ...errorState.contractErrors,
                creditCardTypeErrors: [],
                errorMyCreditCardId: '',
                myCreditCardSecurityCodeErrors: [],
              }}
            />
          </>)}

        </PIMStack>

        <p className="text-em_01">上記の内容に間違いがなければ<br/>『登録情報の確認に進む』ボタンを押してください。</p>

        <PIMPageActions>
          <PIMSubmitButton
            testId={'confirm-registration-information-button'}
            disabled={isSubmitButtonDisabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement>): void => {
              const eventTarget = e.target as HTMLElement
              eventTarget.blur()
              goToMembersSignupInputConfirmPage()
            }}
          >
            登録情報の確認に進む
          </PIMSubmitButton>
        </PIMPageActions>
      </div>)}
    </MembersPageTemplate>
  )
}

const isInvalidLocationState = (
  locationState: {
    memberInfo: MemberInfoForInputPage,
    signboard: Signboard
    paymentMethodList: PaymentMethod[]
  }): boolean => {
  if (!locationState?.memberInfo?.mail) {
    return true
  }
  if (!locationState?.memberInfo?.invitationId) {
    return true
  }
  if (!locationState?.signboard?.signupKey) {
    return true
  }
  if (!locationState?.signboard?.logoImgUrl) {
    return true
  }
  if (!locationState?.signboard?.signupName) {
    return true
  }
  if (!locationState?.signboard?.mailMagazineList) {
    return true
  }
  // noinspection RedundantIfStatementJS
  if (!locationState?.paymentMethodList) {
    return true
  }
  return false
}

const getDefaultEnrollmentReasonSelectedInfo = (
  mainService: Optional<BucketHeader>,
): Optional<EnrollmentReasonSelectedInfo> => {
  if (
    !mainService
    || !mainService.enrollmentReasonList
    || mainService.enrollmentReasonList.length === 0
  ) {
    return undefined
  }

  return {bucketId: mainService.id, enrollmentReasonId: undefined}
}

const getDefaultExtendMetaDataInputInfo = (
  extendMetaData?: ExtendMetaData[],
): Optional<ExtendMetaDataInputInfo> => {
  if (extendMetaData === undefined || extendMetaData.length === 0) {
    return undefined
  }

  const defaultInputInfo: ExtendMetaDataInputInfo = {}
  extendMetaData.map(block => {
    block.items.map(item => {
      switch (item.type) {
        case 'singleSelect':
          defaultInputInfo[item.id] = item.selected ?? undefined
          break
        case 'singleText':
          defaultInputInfo[item.id] = item.value ?? undefined
          break
        default:
          break
      }
    })
  })
  return defaultInputInfo
}
